import {
  CustomReportVariantType,
  CustomReportVariants,
} from "#batteries-included-components/Layouts/CustomReport/CustomReportDetailLayout.helpers";
import { useUpdateCalculatorResultSavedFilter } from "#hooks/adapters/useCalculatorResults";
import { useUpdateRecordSavedFilter } from "#hooks/adapters/useRecords";
import { useAuthenticatedContext } from "#src/contexts/AuthenticatedContext.helpers";
import {
  Button,
  Dialog,
  DialogProps,
  Form,
  RadioInput,
  TextInput,
  useForm,
} from "@validereinc/common-components";
import {
  SavedFilterStatus,
  type FilterObjectType,
  type SavedFilterStatusType,
} from "@validereinc/domain";
import React from "react";

export const EditCustomReportModal = ({
  onClose,
  filter,
  groupBy,
  metadata,
  name,
  status,
  customReportId,
  variant,
}: Pick<DialogProps, "onClose"> & {
  onSave?: (id: string) => void;
  filter?: FilterObjectType<unknown>;
  groupBy?: string[];
  metadata?: unknown[];
  name?: string;
  status?: string;
  customReportId?: string;
  variant: CustomReportVariantType;
}) => {
  const isOpen = !!customReportId;

  const {
    v2: {
      userInfo: { checkHasPermissions },
    },
  } = useAuthenticatedContext();

  const canWriteCompanyReports = checkHasPermissions(
    "company_custom_reports:write"
  );

  const form = useForm({
    defaultValues: {
      status:
        (canWriteCompanyReports ? status : SavedFilterStatus.DRAFT) ??
        SavedFilterStatus.DRAFT,
      name,
    },
  });

  const onSuccess = () => {
    onClose();
  };

  const updateRecordCustomReport = useUpdateRecordSavedFilter({
    onSuccess,
  });

  const updateEmissionsCustomReport = useUpdateCalculatorResultSavedFilter({
    onSuccess,
  });

  const updateCustomReport =
    variant === CustomReportVariants.EMISSIONS
      ? updateEmissionsCustomReport
      : updateRecordCustomReport;

  const onSubmit = form.handleSubmit((values) => {
    const { name, status } = values;
    updateCustomReport.mutate({
      id: customReportId ?? "",
      data: {
        name,
        status: status as SavedFilterStatusType,
        // @ts-expect-error TODO: fix filter typing
        filter,
        group_by: groupBy,
        // @ts-expect-error TODO: fix filter typing
        metadata,
      },
    });
  });

  return (
    <Dialog
      isOpen={isOpen}
      title="Update Custom Report"
      actionRow={[
        <Button
          key="confirm-save-custom-report"
          variant="primary"
          onClick={onSubmit}
          isLoading={updateCustomReport.isLoading}
        >
          Save
        </Button>,
      ]}
      onClose={onClose}
    >
      <Form {...form}>
        <TextInput
          name="name"
          label="Name"
          placeholder="Unsaved Report"
          isRequired
        />
        <RadioInput
          name="status"
          label="Access"
          isRequired
          options={[
            {
              label: "Save to My Reports",
              value: SavedFilterStatus.DRAFT,
            },
            ...(canWriteCompanyReports
              ? [
                  {
                    label: "Save to Company Reports",
                    value: SavedFilterStatus.ACTIVE,
                  },
                ]
              : []),
          ]}
          valueKey="value"
          labelKey="label"
        />
      </Form>
    </Dialog>
  );
};
